import { Box } from '@mui/material';
import qs from 'qs';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ListHeader from '../components/listHeader/ListHeader';
import FilterSidebar from '../components/common/filters/FilterSidebar';
import { FilterState } from '../components/common/filters/interfaces';
import TemplateModal from '../components/common/TemplateModal/TemplateModal';
import { useDownloadCsv } from '../hooks/useDownloadCsv';
import { useSearchTerm } from '../hooks/useSearchTerm';
import { useSyncPageParams } from '../hooks/useSyncPageParams';
import { useUserSorts } from '../hooks/useUserSorts';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { useGetAllTaskTemplate } from '../services/react-query/task_templates';
import { TaskAvailableColumns, TaskSortParam, useGetAllTasks, UseGetAllTasksParams } from '../services/react-query/tasks';
import { listPageLayoutStyles, listPageTableWrapperStyles } from '../styles/sxStyling';
import { PREFERENCES_VALID_KEYS } from '../types/Preference';
import { Task, TaskItemQueryParams } from '../types/Task';
import { UserConfigurableModule } from '../types/User';
import { Loading } from '../uikit';
import ITable from '../uikit/itable/ITable';
import { TaskColumns, TaskFilters } from '../utility/task';
import ListHeaderActions from '../components/listHeader/ListHeaderActions';

export default function TaskList() {
  const navigate = useNavigate();
  const { currentPage, setCurrentPage } = useSyncPageParams();
  const [showModal, setShowModal] = useState(false);

  const [filterState, setFilterState] = useState<FilterState>(undefined);

  const { userSorts } = useUserSorts<TaskSortParam>(UserConfigurableModule.TASKS);

  const { data: taskTemplateData } = useGetAllTaskTemplate({ params: { per_page: 0 } });
  const taskTemplates = taskTemplateData?.data || [];

  const { data: preferenceData, isLoading: prefLoading } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.EVENT_TYPE, PREFERENCES_VALID_KEYS.TASK_TYPE, PREFERENCES_VALID_KEYS.EVENT_LOCATION],
      },
    },
  });

  const baseParams = {
    search: useSearchTerm(),
    filters: filterState,
    sort: userSorts,
  };

  const taskQueryParams: UseGetAllTasksParams = {
    ...baseParams,
    page: currentPage,
  };

  const { data, isLoading: tasksLoading } = useGetAllTasks({ params: taskQueryParams, enabled: true });

  const { isDownloadingCsv, handleDownloadCsv } = useDownloadCsv({
    params: baseParams,
    controller: UserConfigurableModule.TASKS,
  });

  if (tasksLoading || prefLoading) return <Loading />;

  const tasks = data?.data || [];
  const meta = data?.meta;

  const newWithTemplate = (templateUuid: string | null) => {
    const params: TaskItemQueryParams = {};
    if (templateUuid) params.template = templateUuid;
    navigate(`./new?${qs.stringify(params)}`);
  };
  const availableColumns = TaskColumns.map(({ key, label }) => ({
    key,
    label,
  }));
  return (
    <Box>
      <ListHeader
        title="Tasks"
        rightActions={
          <ListHeaderActions
            module={UserConfigurableModule.TASKS}
            availableColumns={availableColumns}
            isDownloadingCsv={isDownloadingCsv}
            onDownloadCsv={handleDownloadCsv}
            onAdd={() => setShowModal(true)}
          />
        }
      />

      <Box sx={listPageLayoutStyles}>
        {/* sidebar filters */}
        <FilterSidebar
          moduleFilters={TaskFilters(preferenceData, taskTemplates)}
          module={UserConfigurableModule.TASKS}
          activeFilter={filterState}
          setActiveFilter={setFilterState}
        />

        {/* main table area */}
        <Box sx={listPageTableWrapperStyles}>
          <ITable<Task, TaskAvailableColumns>
            availableColumns={TaskColumns}
            data={tasks}
            module={UserConfigurableModule.TASKS}
            page={currentPage}
            count={meta?.total_pages ?? 1}
            onPageChange={(_e, v) => setCurrentPage(v)}
          />
        </Box>
      </Box>

      <TemplateModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onCreate={newWithTemplate}
        templates={taskTemplates}
        title="Please choose which task template to use"
      />
    </Box>
  );
}
